import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@/components/ui/modal';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Spinner } from '@/components/ui/spinner';
import { pricingApi, type PricingRule, type CreatePricingRuleDto } from '../api/pricing.api';
import { FeesCard } from '../components/FeesCard';
import { GstSettingsCard } from '../components/GstSettingsCard';
import toast from 'react-hot-toast';

const fareRuleSchema = z.object({
  vehicle_type: z.string().min(1, 'Vehicle type is required'),
  state: z.string().optional(),
  base_fare: z.coerce.number().min(0),
  base_distance_km: z.coerce.number().min(0),
  per_km_rate: z.coerce.number().min(0),
  commission_percent: z.coerce.number().min(0).max(100),
  is_active: z.boolean(),
});

type FareRuleFormData = z.infer<typeof fareRuleSchema>;

const vehicleOptions = [
  { value: 'bike', label: 'Bike' },
  { value: 'scooty', label: 'Scooty' },
  { value: 'auto', label: 'Auto' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'mini_truck', label: 'Mini Truck' },
];

// Empty value = the Default rule set (applies wherever a state has no rule of
// its own). State names must match what the geocoder returns for a booking's
// pickup (Google's administrative_area_level_1 / standard Indian state names).
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Puducherry', 'Chandigarh', 'Andaman and Nicobar Islands',
  'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep',
];

const stateOptions = [
  { value: '', label: 'Default (all other states)' },
  ...INDIAN_STATES.map((s) => ({ value: s, label: s })),
];

export function FareRulesPage() {
  const queryClient = useQueryClient();
  const [editModal, setEditModal] = useState<{ isOpen: boolean; rule: PricingRule | null }>({ isOpen: false, rule: null });
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; rule: PricingRule | null }>({ isOpen: false, rule: null });

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['pricing-rules'],
    queryFn: pricingApi.getAll,
  });

  const { register, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = useForm<FareRuleFormData>({
    resolver: zodResolver(fareRuleSchema),
    defaultValues: { is_active: true, commission_percent: 20, base_distance_km: 2 },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreatePricingRuleDto) => pricingApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Pricing rule created');
      setEditModal({ isOpen: false, rule: null });
      reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreatePricingRuleDto> }) => pricingApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Pricing rule updated');
      setEditModal({ isOpen: false, rule: null });
      reset();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pricingApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Pricing rule deleted');
      setDeleteDialog({ isOpen: false, rule: null });
    },
  });

  const seedMutation = useMutation({
    mutationFn: pricingApi.seed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Default pricing rules seeded');
    },
  });

  const openCreate = () => {
    reset({ is_active: true, commission_percent: 20, base_distance_km: 2, state: '' });
    setEditModal({ isOpen: true, rule: null });
  };

  const openEdit = (rule: PricingRule) => {
    setValue('vehicle_type', rule.vehicle_type);
    setValue('state', rule.state ?? '');
    setValue('base_fare', parseFloat(rule.base_fare));
    setValue('base_distance_km', parseFloat(rule.base_distance_km));
    setValue('per_km_rate', parseFloat(rule.per_km_rate));
    setValue('commission_percent', parseFloat(rule.commission_percent));
    setValue('is_active', rule.is_active);
    setEditModal({ isOpen: true, rule });
  };

  const onSubmit = (data: FareRuleFormData) => {
    // Empty state => Default rule set (backend normalizes blank to null).
    const payload: CreatePricingRuleDto = { ...data, state: data.state || null, city: 'Bhubaneswar' };
    if (editModal.rule) {
      updateMutation.mutate({ id: editModal.rule.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const vehicleTypeValue = watch('vehicle_type');
  const stateValue = watch('state');
  const isActive = watch('is_active');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pricing Rules"
        description="Fare rates per vehicle type — set a Default, or override it for specific states"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Seed Defaults
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Rule
            </Button>
          </div>
        }
      />

      <FeesCard />

      <GstSettingsCard />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Base Fare</TableHead>
                  <TableHead>Base KM</TableHead>
                  <TableHead>Per KM</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-400">
                      No pricing rules found. Seed defaults or add a rule.
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="capitalize">{rule.vehicle_type.replace('_', ' ')}</TableCell>
                      <TableCell>
                        <Badge variant={rule.state ? 'success' : 'secondary'}>
                          {rule.state || 'Default'}
                        </Badge>
                      </TableCell>
                      <TableCell>₹{parseFloat(rule.base_fare).toFixed(0)}</TableCell>
                      <TableCell>{rule.base_distance_km} km</TableCell>
                      <TableCell>₹{parseFloat(rule.per_km_rate).toFixed(0)}/km</TableCell>
                      <TableCell>{parseFloat(rule.commission_percent).toFixed(0)}%</TableCell>
                      <TableCell>
                        <Badge variant={rule.is_active ? 'success' : 'secondary'}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(rule)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ isOpen: true, rule })}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={editModal.isOpen} onClose={() => setEditModal({ isOpen: false, rule: null })}>
        <ModalHeader onClose={() => setEditModal({ isOpen: false, rule: null })}>
          <ModalTitle>{editModal.rule ? 'Edit Pricing Rule' : 'Create Pricing Rule'}</ModalTitle>
        </ModalHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Type</Label>
                <Select
                  options={vehicleOptions}
                  value={vehicleTypeValue}
                  onChange={(v) => setValue('vehicle_type', v)}
                  error={errors.vehicle_type?.message}
                />
              </div>
              <div>
                <Label>State</Label>
                <Select
                  options={stateOptions}
                  value={stateValue ?? ''}
                  onChange={(v) => setValue('state', v)}
                />
                <p className="text-xs text-gray-400 mt-1">
                  "Default" applies wherever a state has no rule of its own.
                </p>
              </div>
              <div>
                <Label>Base Fare (₹)</Label>
                <Input type="number" {...register('base_fare')} />
                {errors.base_fare && <p className="text-xs text-red-500 mt-1">{errors.base_fare.message}</p>}
              </div>
              <div>
                <Label>Base Distance (km)</Label>
                <Input type="number" step="0.1" {...register('base_distance_km')} />
              </div>
              <div>
                <Label>Per KM Rate (₹)</Label>
                <Input type="number" step="0.5" {...register('per_km_rate')} />
                {errors.per_km_rate && <p className="text-xs text-red-500 mt-1">{errors.per_km_rate.message}</p>}
              </div>
              <div>
                <Label>Commission (%)</Label>
                <Input type="number" step="0.5" {...register('commission_percent')} />
              </div>
              <div className="col-span-2">
                <Switch
                  checked={isActive}
                  onChange={(e) => setValue('is_active', e.target.checked)}
                  label="Active"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => setEditModal({ isOpen: false, rule: null })}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}>
              {editModal.rule ? 'Save Changes' : 'Create Rule'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, rule: null })}
        onConfirm={() => {
          if (deleteDialog.rule) deleteMutation.mutate(deleteDialog.rule.id);
        }}
        title={`Delete pricing rule?`}
        description={
          deleteDialog.rule
            ? `Delete rule for ${deleteDialog.rule.vehicle_type}? This action cannot be undone.`
            : undefined
        }
        confirmText="Delete"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
